import axios from "axios";
import { Token } from "../utils/localStorage.util";
import config from "../config.js";

// Normaliza a baseURL: garante que termine com /api SEM duplicação
const baseURL = (config.REACT_APP_HOST_API_INSS || 'http://localhost:6339').trim();

// Remove todas as barras finais e /api se existir
let baseURLNormalized = baseURL.replace(/\/+$/, ''); // Remove barras finais
baseURLNormalized = baseURLNormalized.replace(/\/api\/?$/, ''); // Remove /api do final (com ou sem barra)

// Adiciona /api apenas uma vez
const baseURLWithApi = `${baseURLNormalized}/api`;

const apiInss = axios.create({
  baseURL: baseURLWithApi,
});

apiInss.interceptors.request.use(
  async (config) => {
    const token = Token.getToken();
    if (token) {
      config.headers.Authorization = token;
    }

    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    // CORREÇÃO CRÍTICA: Garantir que a URL não tenha /api duplicado
    if (config.url && config.baseURL) {
      let fullUrl = `${config.baseURL}${config.url}`;
      
      // Se detectar /api/api, corrigir FORÇANDO remoção da duplicação
      if (fullUrl.includes('/api/api')) {
        // Força remoção de todas as duplicações /api/api -> /api
        fullUrl = fullUrl.replace(/\/api\/api+/g, '/api');
        
        // Extrair partes da URL corrigida
        const urlObj = new URL(fullUrl);
        const pathAfterApi = urlObj.pathname.replace(/^\/api/, '');
        
        // Reconstruir baseURL e url corretamente
        config.baseURL = `${urlObj.protocol}//${urlObj.host}/api`;
        config.url = pathAfterApi || '/';
        
        console.error('[api-inss.service] ⚠️ URL DUPLICADA CORRIGIDA:', {
          original: `${config.baseURL}${config.url}`,
          antes: fullUrl,
          depois: `${config.baseURL}${config.url}`
        });
      }
    }

    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

apiInss.interceptors.response.use(
  (response) => {
    return response.data;
  },

  async function (error) {
    return Promise.reject(error);
  }
);

// ============================================================================
// PRÉVIAS INSS - Operações Básicas
// ============================================================================

export function buscarPreviasInss() {
  return apiInss.get(`/previas/inss`);
}

export function buscarPreviasInssPorTexto(texto) {
  return apiInss.get(`/previas/inss/${texto}`);
}

export function editarPreviasInss(id, body) {
  return apiInss.put(`/previas/inss/${id}`, body);
}

export function criarNovaPreviaInss(body) {
  return apiInss.post(`/previas/inss`, body);
}

// ============================================================================
// PRÉVIAS INSS - Verbas e Categorias
// ============================================================================

export async function buscarVerbasComCategorias(payload) {
  return await apiInss.post(
    `/previas/inss/buscar-categorias-por-verbas`,
    payload
  );
}

export async function salvartVinculoCategoriaVerba(payload) {
  return await apiInss.post(
    `/previas/inss/inserir-vinculo-categoria-verba`,
    payload
  );
}

// ============================================================================
// PRÉVIAS INSS - Datas e Resumos
// ============================================================================
// Datas (dt_abertura, simples_dt_opcao, simples_dt_exclusao) vêm da tabela empresas
// via listarEmpresas (api-empresas). Não usar mais endpoint traz-datas do INSS.

export async function buscarVersaoResumoGeral(schema, payload) {
  return await apiInss.post(
    `/previas/resumo-geral?schema=${schema}`,
    payload
  );
}

// ============================================================================
// PRÉVIAS INSS - Relatórios
// ============================================================================

export async function gerarRelatorioPreviaInssConsolidado(
  idPrevia,
  params = {}
) {
  const qs = [];
  if (params?.inicio) qs.push(`inicio=${encodeURIComponent(params.inicio)}`);
  if (params?.fim) qs.push(`fim=${encodeURIComponent(params.fim)}`);
  if (params?.tipoItem)
    qs.push(`tipoItem=${encodeURIComponent(params.tipoItem)}`);
  const query = qs.length > 0 ? `?${qs.join("&")}` : "";
  return await apiInss.get(
    `/previas/inss/gerar-relatorio-consolidado/INSS/${idPrevia}${query}`
  );
}

// ============================================================================
// PRÉVIAS INSS - Envio
// ============================================================================

export async function enviarPreviaInss(payload) {
  return await apiInss.post(
    `/previas/enviar-previa-inss`,
    payload
  );
}

// ============================================================================
// PRÉVIAS INSS - PDF
// ============================================================================

export function buscarPdfPrevia(id) {
  // If an object with cnpj is provided, call the CNPJ route
  // Example: GET /previas/inss/buscar-pdf/{cnpj}?idCliente=...
  if (id && typeof id === "object") {
    const { cnpj, idCliente } = id;
    if (!cnpj) throw new Error("cnpj is required");
    const cnpjDigits = String(cnpj).replace(/\D/g, "");
    const query = idCliente
      ? `?idCliente=${encodeURIComponent(String(idCliente))}`
      : "";
    return apiInss.get(
      `/previas/inss/buscar-pdf/${encodeURIComponent(cnpjDigits)}${query}`
    );
  }

  // Otherwise assume primitive id (idPrevia or idVersao) and call existing route
  return apiInss.get(`/previas/inss/buscar-pdf/${id}`);
}

export async function gravarPdfPrevia(body) {
  return await apiInss.post(`/previas/inss/gravar-pdf`, body);
}

// ============================================================================
// PRÉVIAS INSS - Versões
// ============================================================================

export async function buscarVersaoGeral(idPreviaVersao) {
  return await apiInss.get(
    `/previas/buscar-versao-geral/${idPreviaVersao}`
  );
}

export function buscarVersoesdaPrevia(tipo, idPrevia) {
  return apiInss.get(`/previas/versao/${tipo}/${idPrevia}`);
}

export function buscarVersaoCompletaPrevia(idPreviaVersao) {
  return apiInss.get(`/previas/buscar-versao-geral/${idPreviaVersao}`);
}

export function criarVersaoPrevia(body) {
  return apiInss.post(`/previas/versao`, body);
}

export function editarVersaoPrevia(body, idVersao) {
  return apiInss.put(`/previas/versao/${idVersao}`, body);
}

// ============================================================================
// PRÉVIAS INSS - Observações
// ============================================================================

export async function criarObservacaoPrevia(body) {
  return await apiInss.post(
    `/previas/inss/observacoes`,
    body
  );
}

export async function listarObservacoesPrevias(idPreviaOrCnpj) {
  return await apiInss.get(
    `/previas/inss/observacoes/list/${encodeURIComponent(
      String(idPreviaOrCnpj)
    )}`
  );
}

export async function buscarObservacao(id) {
  return await apiInss.get(`/previas/inss/observacoes/${id}`);
}

export async function atualizarObservacao(body) {
  return await apiInss.put(`/previas/inss/observacoes`, body);
}

export async function deletarObservacao(id) {
  return await apiInss.delete(`/previas/inss/observacoes/${id}`);
}

// ============================================================================
// PRÉVIAS INSS - Aprovação de Clientes
// ============================================================================

export function aprovarClientePrevias(cnpj, idPreviaOrBody) {
  if (!cnpj) throw new Error("cnpj is required");
  const cnpjDigits = String(cnpj).replace(/\D/g, "");
  const body =
    typeof idPreviaOrBody === "number"
      ? { idPrevia: idPreviaOrBody }
      : idPreviaOrBody && typeof idPreviaOrBody === "object"
      ? idPreviaOrBody
      : {};
  return apiInss.post(
    `/previas/inss/cliente/${encodeURIComponent(cnpjDigits)}/aprovar`,
    body
  );
}

export function reprovarClientePrevias(cnpj, idPreviaOrBody) {
  if (!cnpj) throw new Error("cnpj is required");
  const cnpjDigits = String(cnpj).replace(/\D/g, "");
  const body =
    typeof idPreviaOrBody === "number"
      ? { idPrevia: idPreviaOrBody }
      : idPreviaOrBody && typeof idPreviaOrBody === "object"
      ? idPreviaOrBody
      : {};
  return apiInss.post(
    `/previas/inss/cliente/${encodeURIComponent(cnpjDigits)}/reprovar`,
    body
  );
}

export function solicitarAlteracaoClientePrevias(cnpj, body) {
  if (!cnpj) throw new Error("cnpj is required");
  const cnpjDigits = String(cnpj).replace(/\D/g, "");
  return apiInss.post(
    `/previas/inss/cliente/${encodeURIComponent(
      cnpjDigits
    )}/solicitar-alteracao`,
    body
  );
}

export function buscarObservacaoTributarioPrevia(cnpj) {
  if (!cnpj) throw new Error("cnpj is required");
  const cnpjDigits = String(cnpj).replace(/\D/g, "");
  return apiInss.get(
    `/previas/inss/cliente/${encodeURIComponent(
      cnpjDigits
    )}/tributario/observacao`
  );
}

// ============================================================================
// PRÉVIAS INSS - Documentos Pendentes
// ============================================================================

export function solicitaDocsPrevias(body) {
  return apiInss.post(`/previas/previa-doc-pendente`, body);
}

// ============================================================================
// PROJETOS INSS - Operações Básicas
// ============================================================================

export function iniciarProjetoInss(idPrevia) {
  return apiInss.post(`/projetos/inss/${idPrevia}`);
}

/** Confirma projeto INSS a partir da prévia consolidada (fluxo de aprovação) */
export function confirmarProjetoInss(payload) {
  return apiInss.post("/projetos/confirmar", payload);
}

/** Inicia projeto INSS a partir da prévia consolidada (fluxo de aprovação) */
export function iniciarProjetoPreviaInss(payload) {
  return apiInss.post("/projetos/iniciar", payload);
}

/** Libera prévia INSS para edição (volta ao estado em_edicao) */
export function liberarPreviaProjetoInss(idPreviaInss) {
  return apiInss.post("/projetos/liberar", { idPreviaInss });
}

/** Verifica se já existe projeto confirmado ou iniciado para a prévia */
export function buscarStatusProjetoInss(idPreviaInss) {
  return apiInss.get(`/projetos/status/${idPreviaInss}`);
}

export function buscarProjetos(tipoProjeto) {
  // tipoProjeto esperado: 'inss'
  return apiInss.get(`/projetos/${tipoProjeto}`);
}

// ============================================================================
// PROJETOS INSS - Relatórios
// ============================================================================

export async function gerarRelatorioProjetoInssConsolidado(
  idProjetoVersao,
  params = {}
) {
  const qs = [];
  if (params?.inicio) qs.push(`inicio=${encodeURIComponent(params.inicio)}`);
  if (params?.fim) qs.push(`fim=${encodeURIComponent(params.fim)}`);
  if (params?.tipoItem)
    qs.push(`tipoItem=${encodeURIComponent(params.tipoItem.toLowerCase())}`);
  const query = qs.length > 0 ? `?${qs.join("&")}` : "";
  return await apiInss.get(
    `/projetos/inss/gerar-relatorio-consolidado/INSS/${idProjetoVersao}${query}`
  );
}

// ============================================================================
// PROJETOS INSS - Envio
// ============================================================================

export async function enviarProjetoInss(payload) {
  return await apiInss.post(
    `/projetos/enviar-projeto-inss`,
    payload
  );
}

// ============================================================================
// PROJETOS INSS - Versões
// ============================================================================

export function buscarVersoesProjeto(tipo, idProjeto) {
  return apiInss.get(`/projetos/versao/${tipo}/${idProjeto}`);
}

export function buscarVersaoCompletaProjeto(idProjetoVersao) {
  return apiInss.get(`/projetos/buscar-versao-projeto/${idProjetoVersao}`);
}

export function criarVersaoProjeto(body) {
  // Para INSS sempre usa o endpoint padrão
  return apiInss.post(`/projetos/versao`, body);
}

export function editarVersaoProjeto(body, idVersao) {
  return apiInss.put(`/projetos/versao/${idVersao}`, body);
}

export default apiInss;

